import {
  atualizarPasso,
  excluirCampanha,
  excluirLeads,
  listarCampanhas,
  salvarCampanha,
  salvarLeads,
  type CampanhaLocal,
  type LeadLocal,
} from './dados-service';

/**
 * ============================================================================
 * ADAPTADOR DE MIGRAÇÃO — nasce obsoleto, de propósito
 * ============================================================================
 *
 * ⚠️  NÃO ESCREVA CÓDIGO NOVO CONTRA ESTE ARQUIVO.
 *
 * `campaign-service.ts` (1.178 linhas) e `lead-service.ts` (601) têm cerca de
 * vinte chamadas ao antigo cliente Supabase, quase todas dentro de
 * `syncToAll(async (client) => …)`. Reescrever as vinte de uma vez, em arquivos
 * sem nenhum teste automatizado, trocaria uma falha conhecida por um conjunto de
 * falhas desconhecidas.
 *
 * Então este adaptador oferece a MESMA FORMA de chamada — e só ela — traduzindo
 * cada operação para o contrato da API. O navegador continua sem falar com banco
 * nenhum: o que ele fala é HTTP com a API.
 *
 * O que ele deliberadamente NÃO faz:
 *   - não aceita tabela fora da lista abaixo;
 *   - não aceita filtro arbitrário;
 *   - não tem `select()` genérico.
 *
 * Isso é o que o separa de um cliente PostgREST: aqui não dá para inventar
 * consulta nova a partir da tela. Cada operação suportada existe porque um ponto
 * de chamada específico precisava dela.
 *
 * PARA REMOVER: troque cada `syncToAll(async (client) => …)` por uma chamada
 * direta às funções de `dados-service.ts` e apague este arquivo. O trabalho é
 * mecânico e pode ser feito um ponto por vez, sem virada de chave.
 */

type Registro = Record<string, unknown>;

interface Resultado {
  data: Registro[] | null;
  error: { message: string; code?: string } | null;
}

const ok = (data: Registro[] | null = null): Resultado => ({ data, error: null });

function naoSuportado(operacao: string): never {
  // Falha ALTO. O modo de falha que este trabalho está removendo é justamente o
  // erro engolido: um `catch {}` vazio que fazia a tela mostrar sucesso e nada
  // ser gravado, por meses.
  throw new Error(
    `adaptador-migracao: operação não suportada — ${operacao}. ` +
      'Use as funções nomeadas de dados-service.ts.',
  );
}

/** Estado acumulado de uma cadeia `.update(...).eq(...)`. */
interface Cadeia {
  tabela: string;
  operacao: 'update' | 'delete' | 'select';
  valores?: Registro;
}

function finalizar(c: Cadeia, filtro: Registro): Promise<Resultado> {
  if (c.tabela === 'campaigns') {
    const id = String(filtro.id ?? '');
    if (c.operacao === 'delete') return excluirCampanha(id).then(() => ok());
    if (c.operacao === 'update') {
      // O update de campanha precisa dos passos para o upsert transacional.
      // Buscar-e-regravar é mais caro que um UPDATE parcial, e por isso este
      // caminho é de migração: quem quiser eficiência usa `salvarCampanha`.
      return listarCampanhas().then((todas) => {
        const atual = todas.find((x) => x.id === id);
        if (!atual) return ok();
        const v = c.valores ?? {};
        return salvarCampanha(
          {
            ...atual,
            name: (v.name as string) ?? atual.name,
            status: (v.status as string) ?? atual.status,
            currentStepIndex:
              (v.current_step_index as number) ?? atual.currentStepIndex,
            totalLeads: (v.total_leads as number) ?? atual.totalLeads,
            autoEnrich: (v.auto_enrich as boolean) ?? atual.autoEnrich,
          },
          false,
        ).then(() => ok());
      });
    }
    if (c.operacao === 'select') {
      return listarCampanhas().then((todas) =>
        ok(todas.filter((x) => x.id === String(filtro.id)) as unknown as Registro[]),
      );
    }
  }

  if (c.tabela === 'campaign_steps') {
    const campanhaId = String(filtro.campaign_id ?? '');
    if (c.operacao === 'delete') {
      // Apagar todos os passos de uma campanha é sempre seguido de reinserção
      // nos pontos de chamada. Aqui vira no-op, e a reinserção usa
      // `substituirPassos: true` — uma transação em vez de duas escritas com um
      // estado intermediário de "campanha sem passo" visível para todo mundo.
      return Promise.resolve(ok());
    }
    if (c.operacao === 'update') {
      const v = c.valores ?? {};
      return listarCampanhas().then((todas) => {
        const camp = todas.find((x) => x.id === campanhaId);
        const passo = camp?.steps.find(
          (s) =>
            s.targetId === String(filtro.target_id ?? '') &&
            s.regionId === String(filtro.region_id ?? ''),
        );
        if (!camp || !passo?.id) return ok();
        return atualizarPasso(camp.id, passo.id, {
          status: v.status as string | undefined,
          leadsFound: v.leads_found as number | undefined,
          completedAt: v.completed_at ? new Date(String(v.completed_at)).getTime() : undefined,
          erro: (v.erro as string | undefined) ?? null,
        }).then(() => ok());
      });
    }
  }

  if (c.tabela === 'leads' && c.operacao === 'delete') {
    if (typeof filtro.place_id === 'string') {
      return excluirLeads([filtro.place_id]).then(() => ok());
    }
    if (Array.isArray(filtro.place_id)) {
      return excluirLeads(filtro.place_id as string[]).then(() => ok());
    }
  }

  return naoSuportado(`${c.operacao} em ${c.tabela} com filtro ${JSON.stringify(filtro)}`);
}

function construirCadeia(c: Cadeia) {
  return {
    eq(coluna: string, valor: unknown) {
      const p = finalizar(c, { [coluna]: valor });
      return Object.assign(p, {
        maybeSingle: () => p.then((r) => ({ data: r.data?.[0] ?? null, error: r.error })),
        single: () => p.then((r) => ({ data: r.data?.[0] ?? null, error: r.error })),
        select: () => p,
      });
    },
    in(coluna: string, valores: unknown[]) {
      return finalizar(c, { [coluna]: valores });
    },
    match(filtro: Registro) {
      return finalizar(c, filtro);
    },
  };
}

/** Um "client" com a superfície mínima que os pontos de chamada antigos usam. */
export const clienteMigracao = {
  from(tabela: string) {
    return {
      insert(valores: Registro | Registro[]) {
        return inserirOuAtualizar(tabela, valores, false);
      },
      upsert(valores: Registro | Registro[]) {
        return inserirOuAtualizar(tabela, valores, true);
      },
      update(valores: Registro) {
        return construirCadeia({ tabela, operacao: 'update', valores });
      },
      delete() {
        return construirCadeia({ tabela, operacao: 'delete' });
      },
      select(_colunas?: string) {
        return construirCadeia({ tabela, operacao: 'select' });
      },
    };
  },
};

async function inserirOuAtualizar(
  tabela: string,
  valores: Registro | Registro[],
  _upsert: boolean,
): Promise<Resultado> {
  const linhas = Array.isArray(valores) ? valores : [valores];
  if (linhas.length === 0) return ok();

  if (tabela === 'campaigns') {
    for (const l of linhas) {
      await salvarCampanha(
        {
          id: String(l.id),
          name: String(l.name ?? 'Sem nome'),
          status: String(l.status ?? 'draft'),
          currentStepIndex: Number(l.current_step_index ?? 0),
          totalLeads: Number(l.total_leads ?? 0),
          autoEnrich: Boolean(l.auto_enrich),
          createdAt: l.createdAt ? new Date(String(l.createdAt)).getTime() : Date.now(),
          updatedAt: Date.now(),
          steps: [],
        },
        false,
      );
    }
    return ok();
  }

  if (tabela === 'campaign_steps') {
    // Agrupa por campanha: uma transação por campanha em vez de N escritas.
    const porCampanha = new Map<string, CampanhaLocal['steps']>();
    for (const l of linhas) {
      const id = String(l.campaign_id ?? '');
      const lista = porCampanha.get(id) ?? [];
      lista.push({
        targetId: String(l.target_id ?? ''),
        targetName: String(l.target_name ?? ''),
        regionId: String(l.region_id ?? ''),
        regionName: String(l.region_name ?? ''),
        cityName: String(l.city_name ?? ''),
        status: String(l.status ?? 'pending'),
        leadsFound: Number(l.leads_found ?? 0),
        lat: (l.lat as number | null) ?? null,
        lng: (l.lng as number | null) ?? null,
        completedAt: l.completed_at ? new Date(String(l.completed_at)).getTime() : null,
      });
      porCampanha.set(id, lista);
    }

    const todas = await listarCampanhas();
    for (const [id, passos] of porCampanha) {
      const atual = todas.find((c) => c.id === id);
      if (!atual) {
        console.warn(`adaptador-migracao: campanha ${id} não existe; passos ignorados`);
        continue;
      }
      await salvarCampanha({ ...atual, steps: passos }, false);
    }
    return ok();
  }

  if (tabela === 'leads') {
    const leads: LeadLocal[] = linhas.map((l) => ({
      place_id: String(l.place_id),
      name: String(l.name ?? ''),
      formatted_address: (l.formatted_address as string) ?? null,
      formatted_phone_number: (l.formatted_phone_number as string) ?? null,
      website: (l.website as string) ?? null,
      rating: (l.rating as number) ?? null,
      user_ratings_total: (l.user_ratings_total as number) ?? null,
      types: (l.types as string[]) ?? [],
      campaignId: (l.campaign_id as string) ?? null,
      geometry: l.geometry ?? null,
      photos: (l.photos as string[]) ?? [],
      whatsappData: l.whatsapp_data ?? null,
      scannedAt: l.scanned_at ? new Date(String(l.scanned_at)).getTime() : Date.now(),
    }));
    await salvarLeads(leads);
    return ok();
  }

  return naoSuportado(`insert/upsert em ${tabela}`);
}
